import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAiKeys, createAiKey, updateAiKey, deleteAiKey } from '@/services/api/ai-key';
import { getCurrentUser } from '@/services/api/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
    Pencil, Key, CheckCircle2, Eye, EyeOff,
    Copy, ExternalLink, HelpCircle, AlertCircle, RefreshCw,
    BrainCircuit, Sparkles, Zap, BarChart3
} from 'lucide-react';
import type { AiKey, AiKeyCreate, AiKeyUpdate } from '@/types/aiKey';
import { cn } from '@/lib/utils';
import AiTokenUsage from './AiTokenUsage';

interface ProviderMetadata {
    id: string;
    name: string;
    description: string;
    icon: React.ElementType;
    iconBg: string;
    models: string[];
    howToUrl: string;
    docUrl: string;
    steps: {
        title: string;
        link: string;
    }[];
}

const PROVIDERS: ProviderMetadata[] = [
    {
        id: 'openai',
        name: 'OpenAI',
        description: 'Modelos GPT-4o, o1 e DALL-E 3 mais recentes',
        icon: BrainCircuit,
        iconBg: 'bg-[#10a37f]/10 text-[#10a37f]',
        models: ['o1', 'o1-mini', 'GPT-4o', 'GPT-4o-mini'],
        howToUrl: 'https://platform.openai.com/api-keys',
        docUrl: 'https://platform.openai.com/docs',
        steps: [
            { title: '1. Criar Conta', link: 'https://platform.openai.com/signup' },
            { title: '2. Gerar API Key', link: 'https://platform.openai.com/api-keys' },
        ]
    },
    {
        id: 'claude',
        name: 'Claude (Anthropic)',
        description: 'Modelos Claude 3.5 Sonnet e Opus de alta performance',
        icon: Sparkles,
        iconBg: 'bg-[#d97757]/10 text-[#d97757]',
        models: ['Claude 3.5 Sonnet', 'Claude 3 Opus', 'Claude 3 Sonnet'],
        howToUrl: 'https://console.anthropic.com/settings/keys',
        docUrl: 'https://docs.anthropic.com/',
        steps: [
            { title: '1. Criar Conta', link: 'https://console.anthropic.com/' },
            { title: '2. Gerar API Key', link: 'https://console.anthropic.com/settings/keys' },
        ]
    },
    {
        id: 'gemini',
        name: 'Gemini (Google)',
        description: 'Google AI Studio e modelos Pro/Flash',
        icon: Zap,
        iconBg: 'bg-[#1a73e8]/10 text-[#1a73e8]',
        models: ['Gemini 2.0 Flash', 'Gemini 1.5 Pro', 'Gemini 1.5 Flash'],
        howToUrl: 'https://aistudio.google.com/app/apikey',
        docUrl: 'https://ai.google.dev/docs',
        steps: [
            { title: '1. Criar Conta', link: 'https://aistudio.google.com/' },
            { title: '2. Gerar API Key', link: 'https://aistudio.google.com/app/apikey' },
        ]
    },
    {
        id: 'grok',
        name: 'Grok (xAI)',
        description: 'Inteligência em tempo real via xAI API',
        icon: BrainCircuit,
        iconBg: 'bg-[#000000]/10 text-[#000000]',
        models: ['Grok-2-1212', 'Grok-2', 'Grok-2-mini'],
        howToUrl: 'https://console.x.ai/',
        docUrl: 'https://docs.x.ai/',
        steps: [
            { title: '1. Criar Conta', link: 'https://console.x.ai/' },
            { title: '2. Gerar API Key', link: 'https://console.x.ai/' },
        ]
    },
    {
        id: 'deepseek',
        name: 'DeepSeek',
        description: 'Modelos eficientes e de baixo custo',
        icon: BrainCircuit,
        iconBg: 'bg-[#007aff]/10 text-[#007aff]',
        models: ['DeepSeek-V3', 'DeepSeek-R1', 'DeepSeek-Coder'],
        howToUrl: 'https://platform.deepseek.com/api_keys',
        docUrl: 'https://api-docs.deepseek.com/',
        steps: [
            { title: '1. Criar Conta', link: 'https://platform.deepseek.com/' },
            { title: '2. Gerar API Key', link: 'https://platform.deepseek.com/api_keys' },
        ]
    }
];

export default function AiKeys() {
    const user = getCurrentUser();
    const userId = user?.id;
    const [activeTab, setActiveTab] = useState<'keys' | 'usage'>('keys');

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState<ProviderMetadata | null>(null);
    const [editingKey, setEditingKey] = useState<AiKey | null>(null);
    const [visibleKeys, setVisibleKeys] = useState<Set<number>>(new Set());
    const [formData, setFormData] = useState<AiKeyCreate>({
        user_id: userId || 0,
        provider: '',
        api_key: '',
        is_active: true,
    });

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const { data: keys, isLoading, error } = useQuery({
        queryKey: ['ai-keys'],
        queryFn: getAiKeys,
    });

    const createMutation = useMutation({
        mutationFn: createAiKey,
        onSuccess: () => {
            toast({ title: 'Chave configurada!', description: 'Conexão com a IA estabelecida com sucesso.' });
            queryClient.invalidateQueries({ queryKey: ['ai-keys'] });
            setIsDialogOpen(false);
        },
        onError: (error: any) => {
            toast({ variant: 'destructive', title: 'Erro ao configurar', description: error.response?.data?.detail?.[0]?.message || error.response?.data?.message || 'Falha ao salvar chave.' });
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: AiKeyUpdate }) => updateAiKey(id, data),
        onSuccess: () => {
            toast({ title: 'Configuração atualizada!' });
            queryClient.invalidateQueries({ queryKey: ['ai-keys'] });
            setIsDialogOpen(false);
        },
        onError: (error: any) => {
            toast({ variant: 'destructive', title: 'Erro ao atualizar', description: error.response?.data?.detail?.[0]?.message || error.response?.data?.message || 'Falha ao salvar alterações.' });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deleteAiKey,
        onSuccess: () => {
            toast({ title: 'Removido com sucesso' });
            queryClient.invalidateQueries({ queryKey: ['ai-keys'] });
        },
    });

    const configuredKeysMap = useMemo(() => {
        const map = new Map<string, AiKey>();
        keys?.forEach(k => map.set(k.provider.toLowerCase(), k));
        return map;
    }, [keys]);

    const handleOpenModal = (provider: ProviderMetadata, existingKey?: AiKey) => {
        setSelectedProvider(provider);
        if (existingKey) {
            setEditingKey(existingKey);
            setFormData({
                user_id: userId || 0,
                provider: existingKey.provider,
                api_key: existingKey.api_key,
                is_active: existingKey.is_active,
            });
        } else {
            setEditingKey(null);
            setFormData({
                user_id: userId || 0,
                provider: provider.name,
                api_key: '',
                is_active: true,
            });
        }
        setIsDialogOpen(true);
    };

    const toggleKeyVisibility = (id: number) => {
        setVisibleKeys(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        toast({ title: 'Copiado!', description: 'Chave API copiada para a área de transferência.' });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.api_key.trim()) {
            toast({ variant: 'destructive', title: 'Campo obrigatório', description: 'Por favor, insira a chave API.' });
            return;
        }

        if (editingKey) {
            updateMutation.mutate({ id: editingKey.id, data: formData });
        } else {
            createMutation.mutate(formData);
        }
    };

    return (
        <div className="space-y-8 pb-10">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 font-outfit tracking-tight">AI Keys</h1>
                    <p className="mt-1 text-gray-500 text-lg">Gerencie seus provedores de inteligência artificial em um só lugar.</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
                <button
                    onClick={() => setActiveTab('keys')}
                    className={cn(
                        'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                        activeTab === 'keys'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                    )}
                >
                    <Key className="h-4 w-4" />
                    Chaves
                </button>
                <button
                    onClick={() => setActiveTab('usage')}
                    className={cn(
                        'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                        activeTab === 'usage'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                    )}
                >
                    <BarChart3 className="h-4 w-4" />
                    Usage
                </button>
            </div>

            {activeTab === 'usage' ? (
                <AiTokenUsage />
            ) : isLoading ? (
                <div className="flex items-center justify-center p-20">
                    <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
                </div>
            ) : error ? (
                <div className="p-6 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-4 text-red-700">
                    <AlertCircle className="h-6 w-6" />
                    <p className="font-medium">Ocorreu um erro ao carregar seus provedores.</p>
                </div>
            ) : (
                <div className="space-y-12">
                    {/* Configured Section */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-2">
                            <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center">
                                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                            </div>
                            <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">Provedores Configurados</h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {PROVIDERS.filter(p => configuredKeysMap.has(p.id)).map(provider => {
                                const keyData = configuredKeysMap.get(provider.id)!;
                                const isVisible = visibleKeys.has(keyData.id);
                                return (
                                    <div key={provider.id} className="group relative bg-white border border-gray-100 rounded-3xl p-6 shadow-sm hover:shadow-xl hover:border-blue-100 transition-all duration-300">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex items-center gap-3">
                                                <div className={cn("h-12 w-12 rounded-2xl flex items-center justify-center", provider.iconBg)}>
                                                    <provider.icon className="h-6 w-6" />
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-gray-900 leading-tight">{provider.name}</h3>
                                                    <div className="flex items-center gap-1.5 mt-0.5">
                                                        <span className="h-2 w-2 rounded-full bg-green-500"></span>
                                                        <span className="text-[10px] font-bold text-green-600 uppercase tracking-tighter">Configurado</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 rounded-full text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                                onClick={() => handleOpenModal(provider, keyData)}
                                            >
                                                <Pencil className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>

                                        <div className="relative mb-4">
                                            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                                <Key className="h-3.5 w-3.5 text-gray-400" />
                                            </div>
                                            <input
                                                readOnly
                                                type={isVisible ? "text" : "password"}
                                                value={keyData.api_key}
                                                className="w-full bg-gray-50 border-none rounded-xl py-2.5 pl-9 pr-20 text-xs font-mono text-gray-600 focus:ring-1 focus:ring-blue-100"
                                            />
                                            <div className="absolute inset-y-0 right-1.5 flex items-center gap-1">
                                                <button
                                                    onClick={() => toggleKeyVisibility(keyData.id)}
                                                    className="p-1.5 text-gray-400 hover:text-gray-900 transition-colors"
                                                >
                                                    {isVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                </button>
                                                <button
                                                    onClick={() => handleCopy(keyData.api_key)}
                                                    className="p-1.5 text-gray-400 hover:text-gray-900 transition-colors"
                                                >
                                                    <Copy className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-1.5 mb-6">
                                            {provider.models.slice(0, 3).map(m => (
                                                <Badge key={m} variant="secondary" className="px-2 py-0 h-5 text-[10px] font-medium bg-gray-50 text-gray-500 border-gray-100">
                                                    {m}
                                                </Badge>
                                            ))}
                                            {provider.models.length > 3 && (
                                                <Badge variant="secondary" className="px-2 py-0 h-5 text-[10px] font-medium bg-gray-50 text-gray-500 border-gray-100">
                                                    +{provider.models.length - 3}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Not Configured Section */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-2">
                            <div className="h-5 w-5 rounded-full bg-blue-50 flex items-center justify-center">
                                <AlertCircle className="h-3.5 w-3.5 text-blue-600" />
                            </div>
                            <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">Provedores Não Configurados</h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {PROVIDERS.filter(p => !configuredKeysMap.has(p.id)).map(provider => (
                                <button
                                    key={provider.id}
                                    onClick={() => handleOpenModal(provider)}
                                    className="group flex flex-col items-center justify-center p-8 bg-white border-2 border-dashed border-gray-100 rounded-3xl hover:border-blue-400 hover:bg-blue-50/30 transition-all duration-300"
                                >
                                    <div className={cn("h-16 w-16 rounded-2xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110", provider.iconBg)}>
                                        <provider.icon className="h-8 w-8" />
                                    </div>
                                    <h3 className="font-bold text-gray-900 group-hover:text-blue-700">{provider.name}</h3>
                                    <p className="text-[10px] text-gray-400 mt-1">Clique para configurar</p>
                                    <Badge className="mt-4 bg-blue-600 hover:bg-blue-700 border-none transition-transform group-hover:translate-y-[-2px]">
                                        Configurar Agora
                                    </Badge>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Instruction Modal */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-[550px] p-0 overflow-hidden border-none rounded-3xl">
                    {selectedProvider && (
                        <div className="bg-white">
                            <div className="p-8 pb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-3">
                                        <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center", selectedProvider.iconBg)}>
                                            <selectedProvider.icon className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h2 className="text-xl font-bold font-outfit">{selectedProvider.name}</h2>
                                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                                            </div>
                                            <p className="text-xs text-gray-500">{selectedProvider.description}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="px-8 pb-8 space-y-6">
                                {/* Help Box */}
                                <div className="p-5 bg-teal-50/50 border border-teal-100 rounded-2xl">
                                    <div className="flex items-center gap-2 text-teal-800 font-semibold text-sm mb-4">
                                        <HelpCircle className="h-4 w-4" />
                                        Como obter sua API Key:
                                    </div>
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {selectedProvider.steps.map(step => (
                                            <a
                                                key={step.title}
                                                href={step.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 px-3 py-1.5 bg-white border border-teal-100 rounded-lg text-xs text-teal-700 hover:bg-teal-50 transition-colors font-medium shadow-sm"
                                            >
                                                <ExternalLink className="h-3 w-3" />
                                                {step.title}
                                            </a>
                                        ))}
                                        <a
                                            href={selectedProvider.docUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-900 transition-colors font-medium"
                                        >
                                            <AlertCircle className="h-3 w-3" />
                                            Documentação
                                        </a>
                                    </div>
                                    <div className="flex items-start gap-2 text-[11px] text-teal-600/80">
                                        <Sparkles className="h-3 w-3 mt-0.5" />
                                        Crie uma conta {selectedProvider.name} e gere sua API key no dashboard para habilitar as funcionalidades.
                                    </div>
                                </div>

                                {/* Form */}
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Chave API {selectedProvider.name.toUpperCase()}</Label>
                                            {editingKey && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (window.confirm('Excluir esta configuração?')) {
                                                            deleteMutation.mutate(editingKey.id);
                                                            setIsDialogOpen(false);
                                                        }
                                                    }}
                                                    className="text-[10px] font-bold text-red-400 hover:text-red-600 uppercase tracking-wider transition-colors"
                                                >
                                                    Remover Chave
                                                </button>
                                            )}
                                        </div>
                                        <div className="relative group">
                                            <Input
                                                id="api_key"
                                                type="password"
                                                placeholder="Cole sua chave API aqui"
                                                value={formData.api_key}
                                                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                                                className="h-12 border-gray-100 bg-gray-50 group-hover:bg-white group-hover:border-blue-200 focus:bg-white focus:ring-blue-100 focus:border-blue-300 transition-all rounded-xl pl-4 pr-12 text-sm font-mono"
                                            />
                                            <div className="absolute inset-y-0 right-3 flex items-center gap-2">
                                                <button type="button" onClick={() => handleCopy(formData.api_key)} className="p-1 text-gray-400 hover:text-gray-900">
                                                    <Copy className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                        <p className="text-[10px] text-gray-400">Cole sua chave API {selectedProvider.name} aqui para permitir que o sistema se conecte.</p>
                                    </div>

                                    <div className="flex flex-wrap gap-1.5 pt-2">
                                        {selectedProvider.models.map(m => (
                                            <Badge key={m} variant="secondary" className="px-2 py-0 h-5 text-[10px] font-medium bg-gray-50 text-gray-400 border-gray-100">
                                                {m}
                                            </Badge>
                                        ))}
                                    </div>

                                    <div className="flex gap-3 pt-6">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            className="flex-1 h-11 border-gray-100 text-gray-500 hover:bg-gray-50 rounded-xl font-semibold"
                                            onClick={() => setIsDialogOpen(false)}
                                        >
                                            Cancelar
                                        </Button>
                                        <Button
                                            type="submit"
                                            className="flex-1 h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold font-outfit shadow-lg shadow-blue-200 transition-transform active:scale-95"
                                            disabled={createMutation.isPending || updateMutation.isPending}
                                        >
                                            {createMutation.isPending || updateMutation.isPending ? 'Conectando...' : editingKey ? 'Salvar Alterações' : 'Configurar Provedor'}
                                        </Button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
